import type { AssetInfoProps } from "./asset-info.type";

import { LockOutlined } from "@ant-design/icons";
import { Space } from "antd";
import { useTranslation } from "react-i18next";

import AssetBreadcrumb from "@/modules/asset/asset-breadcrumb";
import { AssetMapSimple } from "@/modules/asset/asset-map-simple";
import { AssetRiskTag } from "@/modules/asset/asset-risk-tag";
import {
  FlexContainer,
  FlexContainerDate,
  FlexContainerText,
  FlexContainerJsx,
  FlexContainerImage,
} from "@/modules/flex-container";
import { InterventionTemplateLabel } from "@/modules/intervention-template/intervention-template-label";
import { TranslateCost } from "@/modules/translate";
import { fromNow } from "@/utilities/date";

import { AssetInfoAssetType } from "./asset-info-asset-type";
import { AssetInfoConditionRating } from "./asset-info-condition-rating";
import { AssetInfoFiles } from "./asset-info-files";
import { AssetInfoLifeSpan } from "./asset-info-life-span";
import { AssetInfoPlanFile } from "./asset-info-plan-file";
import { AssetInfoStudy } from "./asset-info-study";

export const AssetInfo = ({ asset }: AssetInfoProps) => {
  const { t } = useTranslation();

  const {
    name,
    description,
    reference,
    asset_type_id,
    manufacturer,
    model,
    serial,
    date_manufacture,
    date_service,
    replacement_cost,
    technical_info,
    asset_info,
    cartesian_image,
    files,
    ancestors,
    cmms_id,
    erp_id,
    attendance_token,
    plan_file,
    default_template,
  } = asset;

  return (
    <Space orientation="vertical" size="middle">
      {name && (
        <FlexContainerText value={name} label={t("asset:form.name.label")} />
      )}
      {description && (
        <FlexContainerText
          value={description}
          label={t("asset:form.description.label")}
        />
      )}
      {reference && (
        <FlexContainerText
          value={reference}
          label={t("asset:form.referenceNumber.label")}
        />
      )}
      <AssetInfoAssetType asset_type_id={asset_type_id} />
      <AssetInfoConditionRating asset={asset} />
      <FlexContainerText
        label={t("asset:risk.label")}
        value={<AssetRiskTag asset={asset} size="large" />}
      />
      <AssetInfoStudy asset={asset} />
      {manufacturer && (
        <FlexContainerText
          value={manufacturer}
          label={t("asset:form.manufacturer.label")}
        />
      )}
      {model && (
        <FlexContainerText value={model} label={t("asset:form.model.label")} />
      )}
      {serial && (
        <FlexContainerText
          value={serial}
          label={t("asset:form.serialNumber.label")}
        />
      )}
      {date_manufacture && (
        <FlexContainerDate
          value={date_manufacture}
          label={t("asset:form.manufactureDate.label")}
        />
      )}
      {date_service && (
        <FlexContainerDate
          value={date_service}
          label={t("asset:form.serviceDate.label")}
        />
      )}
      {replacement_cost && (
        <FlexContainerText
          label={t("asset:form.replacementCost.label")}
          value={<TranslateCost value={replacement_cost} />}
        />
      )}
      <AssetInfoLifeSpan asset={asset} />
      {technical_info && (
        <FlexContainerText
          value={technical_info}
          label={t("asset:form.technicalInfo.label")}
        />
      )}
      {asset_info && (
        <FlexContainerText
          value={asset_info}
          label={t("asset:form.assetInfo.label")}
        />
      )}
      <FlexContainerJsx
        label={t("global:coordinate.label")}
        value={<AssetMapSimple asset={asset} context="asset_info" />}
        fullWidth
      />
      {cartesian_image && (
        <FlexContainerImage
          label={t("asset:form.assetPicture.label")}
          document={cartesian_image}
        />
      )}
      <AssetInfoPlanFile planFile={plan_file} />
      <AssetInfoFiles files={files} />

      {ancestors && (
        <FlexContainerJsx
          value={<AssetBreadcrumb asset={asset} exclude={["organization"]} />}
          label={t("global:hierarchy")}
        />
      )}
      {cmms_id && (
        <FlexContainerText
          value={cmms_id}
          label={t("asset:form.cmmsId.label")}
        />
      )}
      {erp_id && (
        <FlexContainerText value={erp_id} label={t("asset:form.erpId.label")} />
      )}
      {date_manufacture && (
        <FlexContainerText
          value={fromNow(date_manufacture, true)}
          label={t("asset:form.age.label")}
        />
      )}
      {attendance_token && (
        <FlexContainer
          label={t("asset:form.attendanceToken.label")}
          value={t("asset:form.attendanceToken.tokenProvided")}
          icon={<LockOutlined />}
        />
      )}
      {default_template && (
        <InterventionTemplateLabel template={default_template} />
      )}
    </Space>
  );
};
